import { AppUtils } from '@core/utilities';
import { ITreeNode } from '../interfaces/tree-node';

/**
 * @description
 * Recursively searches the tree at any depth for a node whose `key` matches,
 * then returns a **new** tree array with that node replaced by a shallow-spread
 * copy merged with `patch`.
 *
 * Every ancestor node on the path to the changed node also receives a new
 * object reference, so Angular OnPush change detection fires correctly all the
 * way up the component tree.  Branches that do not contain the target node are
 * returned as-is (original references — no wasted re-renders).
 *
 * @param nodes  The current tree array.
 * @param key    The `key` of the node to update.
 * @param patch  Partial properties to merge into the matched node.
 * @returns      A new tree array with the patched node (and new ancestor references).
 */
export function patchTreeNode<T = unknown>(
  nodes: ITreeNode<T>[],
  key: string,
  patch: Partial<ITreeNode<T>>,
): ITreeNode<T>[] {
  return nodes.map((node) => {
    // Found — return a brand-new object so OnPush detects the change
    if (node.key === key) {
      return { ...node, ...patch };
    }

    // Has children — recurse and propagate a new reference only if something changed
    if (node.children?.length) {
      const updatedChildren = patchTreeNode(node.children as ITreeNode<T>[], key, patch);
      // If nothing changed in this subtree, keep the original reference
      if (updatedChildren === node.children) return node;
      return { ...node, children: updatedChildren };
    }

    return node;
  });
}

/**
 * @description Recursively flattens a hierarchical tree array into a single-dimensional array.
 * Useful for keyboard navigation and rendering flattened lists.
 * @param nodes The array of tree nodes to flatten.
 * @returns A flat array of all nodes and their descendants.
 */
export function flattenTree<TData = unknown>(nodes: ITreeNode<TData>[]): ITreeNode<TData>[] {
  let flatList: ITreeNode<TData>[] = [];
  for (const node of nodes) {
    flatList.push(node);
    if (node.children && node.children.length > 0) {
      flatList = flatList.concat(flattenTree(node.children));
    }
  }
  return flatList;
}

/**
 * @description Returns the ancestor chain of `targetKey` walking from the deepest parent
 * up to the root. Returns an empty array if `targetKey` is a root or not found.
 * @param rootNodes The root-level nodes to walk.
 * @param targetKey The node key whose ancestors should be returned.
 */
export function findAncestors<TData = unknown>(rootNodes: ITreeNode<TData>[], targetKey: string): ITreeNode<TData>[] {
  for (const node of rootNodes) {
    if (!node.children?.length) continue;
    if (node.children.some(c => c.key === targetKey)) return [node];
    const deeper = findAncestors(node.children, targetKey);
    if (deeper.length) return [...deeper, node];
  }
  return [];
}

/**
 * @description Pure cascade selection for checkbox mode.
 * Toggling `node` toggles all of its selectable descendants too; ancestors are auto-included
 * only when **all** of their selectable descendants are included. The indeterminate visual
 * state is derived elsewhere (in the node component) — not encoded here.
 *
 * @param node The toggled node.
 * @param current The selection array before the toggle (or null).
 * @param rootNodes The current tree root (needed for ancestor traversal).
 * @returns The new selection array, or null when empty.
 */
export function cascadeCheckboxSelection<TData = unknown>(
  node: ITreeNode<TData>,
  current: ITreeNode<TData>[] | null,
  rootNodes: ITreeNode<TData>[],
): ITreeNode<TData>[] | null {

  // non-selectable nodes are inert
  if (node.selectable === false) return current;

  // build a key set from the current selection
  const selectedKeys = new Set((current ?? []).map(n => n.key));

  // new state for the toggled node
  const willCheck = !selectedKeys.has(node.key);

  // toggle node + all selectable descendants in one pass
  for (const n of flattenTree([node])) {
    if (n.selectable === false) continue;
    if (willCheck) selectedKeys.add(n.key);
    else selectedKeys.delete(n.key);
  }

  // walk ancestors; include them only when ALL their selectable descendants are included
  for (const ancestor of findAncestors(rootNodes, node.key)) {
    const desc = flattenTree(ancestor.children ?? []).filter(n => n.selectable !== false);
    const allChecked = desc.length > 0 && desc.every(d => selectedKeys.has(d.key));
    if (allChecked) selectedKeys.add(ancestor.key);
    else selectedKeys.delete(ancestor.key);
  }

  // resolve keys back to node refs in tree order
  const refs = flattenTree(rootNodes).filter(n => selectedKeys.has(n.key));
  return refs.length ? refs : null;
}

/**
 * @description Filters a tree structure based on a search query, preserving parent paths.
 * @param nodes The original tree nodes.
 * @param query The search text to match.
 * @param filterBy The property key on the node to search against (e.g., 'label').
 * @returns A new filtered array of tree nodes.
 */
export function filterTree<TData = unknown>(nodes: ITreeNode<TData>[], query: string, filterBy: keyof ITreeNode = 'label'): ITreeNode<TData>[] {
  if (!query) return nodes;

  const lowerQuery = query.toLowerCase();
  const filtered: ITreeNode<TData>[] = [];

  for (const node of nodes) {
    const rawValue = AppUtils.chainByReducerFn(filterBy, node);
    const stringValue = rawValue != null ? String(rawValue).toLowerCase() : '';
    const isMatch = stringValue.includes(lowerQuery);
    
    let filteredChildren: ITreeNode<TData>[] = [];
    if (node.children) {
      filteredChildren = filterTree(node.children, query, filterBy);
    }

    if (isMatch || filteredChildren.length > 0) {
      filtered.push({
        ...node,
        children: filteredChildren.length > 0 ? filteredChildren : node.children,
        expanded: filteredChildren.length > 0 ? true : node.expanded,
      });
    }
  }
  return filtered;
}